import { z } from 'zod';
// תוספות וקיזוזים ידניים — amounts the operator types onto a hugim staff
// member's settings, outside the receipts channel. A row pays (addition) or
// takes away (offset) the SAME amount in each of its months: `months` is a
// multiplier, never a divisor — 1,200 × 3 months = 1,200 in each, 3,600 in all.
//
// Not receipt-based, so no refund, credit or cancellation touches it, and none
// of the percentage levels applies to it. A closed month keeps what it froze;
// an edit or a delete only reaches months that are still open (or re-opened).
//
// The month is the payroll month the operator sees — the month a period is
// labelled with (its end month; the same month as its start on a calendar
// month box).
export const MANUAL_ADJ_DESCRIPTION_MAX = 120;
/** A spread longer than this asks the operator to confirm before it saves. */
export const MANUAL_ADJ_CONFIRM_MONTHS = 24;
export const ManualAdjustmentKindSchema = z.enum(['addition', 'offset']);
const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be YYYY-MM');
/** What the operator enters. `amount` is always positive — an offset is
 *  entered positive and subtracted; a negative figure is rejected, never
 *  flipped silently. */
export const ManualAdjustmentInputSchema = z.object({
    kind: ManualAdjustmentKindSchema,
    description: z.string().trim().min(1).max(MANUAL_ADJ_DESCRIPTION_MAX),
    amount: z
        .number()
        .positive()
        .max(10_000_000)
        .refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-6, 'at most two decimals'),
    start_month: MonthSchema,
    months: z.number().int().min(1).max(1200),
});
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** 'YYYY-MM' + n months. */
export function addMonthKey(month, n) {
    const [y, m] = month.split('-').map(Number);
    const idx = y * 12 + (m - 1) + n;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}
/** Months from a to b ('YYYY-MM'); negative when b is before a. */
export function monthDiff(a, b) {
    const [ay, am] = a.split('-').map(Number);
    const [by, bm] = b.split('-').map(Number);
    return (by - ay) * 12 + (bm - am);
}
/** 1-based position of `month` in the row's spread, or null outside it. */
export function manualMonthIndex(row, month) {
    const i = monthDiff(row.start_month, month);
    return i >= 0 && i < row.months ? i + 1 : null;
}
/** The row's months, first to last. */
export function manualAdjustmentMonths(row) {
    return Array.from({ length: row.months }, (_, i) => addMonthKey(row.start_month, i));
}
/** The earliest month a NEW start month may be: the month before the open
 *  month (it can still be unfinished), never further back — a row starting in
 *  an old month that nobody runs any more would pay nothing. Closed months are
 *  refused on top of this. */
export function earliestStartMonth(openMonth) {
    return addMonthKey(openMonth, -1);
}
/** Why a start month can't be chosen, or null when it can. `keep` is the row's
 *  current start month on an edit — a spread already under way keeps it. */
export function startMonthProblem(start, openMonth, closedMonths, keep) {
    if (keep != null && start === keep)
        return null;
    if (closedMonths.includes(start))
        return 'closed';
    if (monthDiff(earliestStartMonth(openMonth), start) < 0)
        return 'past';
    return null;
}
/** The rows that fall on `month` — additions first, then offsets, each in the
 *  order they were entered (ascending id). */
export function manualLinesForMonth(rows, month) {
    const out = [];
    for (const kind of ['addition', 'offset']) {
        for (const r of [...rows].filter((x) => x.kind === kind).sort((a, b) => a.id - b.id)) {
            const idx = manualMonthIndex(r, month);
            if (idx == null)
                continue;
            out.push({
                id: r.id,
                kind: r.kind,
                description: r.description,
                amount: round2(r.amount),
                month_index: idx,
                months: r.months,
            });
        }
    }
    return out;
}
/** "חודש 2 מתוך 3" — where a month's line sits in its spread. */
export function manualSpreadLabel(l) {
    return `חודש ${l.month_index} מתוך ${l.months}`;
}
/** Totals of a month's lines; both positive. */
export function sumManualLines(lines) {
    let additions = 0;
    let offsets = 0;
    for (const l of lines) {
        if (l.kind === 'addition')
            additions += l.amount;
        else
            offsets += l.amount;
    }
    return { additions: round2(additions), offsets: round2(offsets) };
}
/** The course part of a hugim computation's `hug_pay` — what "שכר חוגים" shows.
 *  `hug_pay` is the grand total; add-ons and the manual additions/offsets are
 *  subsets of it, split out for display. Every field may be absent on a
 *  snapshot frozen before it existed. */
export function hugCoursePay(c) {
    return round2((c.hug_pay ?? 0) - (c.addons_pay ?? 0) - (c.manual_additions ?? 0) + (c.manual_offsets ?? 0));
}
//# sourceMappingURL=manual-adjustments.js.map